// Shared date/time utilities
export class DateTimeUtils {
    static toTimestamp(value?: string | Date | null): number {
        if (!value) return 0;
        const parsed = value instanceof Date ? value.getTime() : new Date(value).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
    }

    static formatRemainingTime(endDate?: string | Date | null): string {
        const target = typeof endDate === 'string' ? new Date(endDate) : endDate;
        if (!(target instanceof Date) || Number.isNaN(target.getTime())) {
            return '--';
        }

        const diff = target.getTime() - Date.now();
        if (diff <= 0) {
            return '00:00';
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (days > 0) {
            return `${days} dias, ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    static dateToString(date: Date, format = 'YYYY-MM-DD HH:mm:ss'): string {
        const map: Record<string, string> = {
            YYYY: date.getFullYear().toString(),
            MM: (date.getMonth() + 1).toString().padStart(2, '0'),
            DD: date.getDate().toString().padStart(2, '0'),
            HH: date.getHours().toString().padStart(2, '0'),
            mm: date.getMinutes().toString().padStart(2, '0'),
            ss: date.getSeconds().toString().padStart(2, '0'),
        };
        return format.replace(/YYYY|MM|DD|HH|mm|ss/g, (matched) => map[matched]);
    }

    static addDays(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    static addMonths(date: Date, months: number): Date {
        const result = new Date(date);
        result.setMonth(result.getMonth() + months);
        return result;
    }

    static addYears(date: Date, years: number): Date {
        const result = new Date(date);
        result.setFullYear(result.getFullYear() + years);
        return result;
    }

    static differenceInDays(date1: Date, date2: Date): number {
        const timeDiff = date1.getTime() - date2.getTime();
        return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    }

    static parseDate(dateStr: string): Date {
        const parsedDate = new Date(dateStr);
        if (isNaN(parsedDate.getTime())) {
            throw new Error('Invalid date string');
        }
        return parsedDate;
    }

    static startOfDay(date: Date): Date {
        const result = new Date(date);
        result.setHours(0, 0, 0, 0);
        return result;
    }

    static endOfDay(date: Date): Date {
        const result = new Date(date);
        result.setHours(23, 59, 59, 999);
        return result;
    }

    static isSameDay(date1: Date, date2: Date): boolean {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        );
    }

    static isWeekday(date: Date): boolean {
        const day = date.getDay();
        return day >= 1 && day <= 5;
    }

    static nextWeekday(date: Date): Date {
        const result = new Date(date);
        do {
            result.setDate(result.getDate() + 1);
        } while (result.getDay() === 0 || result.getDay() === 6);
        return result;
    }

    // ─── Feriados nacionais brasileiros ─────────────────────────────────────
    // Espelho do BE (cdcb-backend chassis/datetime.utils.ts). Manter sincronizado.
    // Base legal: Lei 662/1949 + 10.607/2002 + 14.759/2023 + Sexta-Santa + Corpus Christi.
    // Estaduais e municipais ficam fora — nao temos tabela por estado/municipio
    // no client. Pregoeiro pode editar manualmente os deadlines auto-populados se
    // houver feriado municipal (RN-S1.1.3).
    static readonly BRAZIL_NATIONAL_FIXED_HOLIDAYS: ReadonlyArray<{ month: number; day: number; name: string }> = [
        { month: 1, day: 1, name: 'Confraternizacao Universal' },
        { month: 4, day: 21, name: 'Tiradentes' },
        { month: 5, day: 1, name: 'Dia do Trabalho' },
        { month: 9, day: 7, name: 'Independencia do Brasil' },
        { month: 10, day: 12, name: 'Nossa Senhora Aparecida' },
        { month: 11, day: 2, name: 'Finados' },
        { month: 11, day: 15, name: 'Proclamacao da Republica' },
        { month: 11, day: 20, name: 'Dia da Consciencia Negra' },
        { month: 12, day: 25, name: 'Natal' },
    ];

    static calculateEasterSunday(year: number): Date {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return new Date(year, month - 1, day);
    }

    static isBrazilianNationalHoliday(date: Date): boolean {
        const d = this.startOfDay(date);
        const month = d.getMonth() + 1;
        const day = d.getDate();
        for (const h of this.BRAZIL_NATIONAL_FIXED_HOLIDAYS) {
            if (h.month === month && h.day === day) return true;
        }
        const easter = this.calculateEasterSunday(d.getFullYear());
        const goodFriday = this.addDays(easter, -2);
        const corpusChristi = this.addDays(easter, 60);
        return this.isSameDay(d, goodFriday) || this.isSameDay(d, corpusChristi);
    }

    static isBrazilianBusinessDay(date: Date): boolean {
        return this.isWeekday(date) && !this.isBrazilianNationalHoliday(date);
    }

    /**
     * Subtrai N dias uteis a partir de uma data, pulando sabados, domingos e
     * feriados nacionais brasileiros. Se `days === 0`, retorna a data original.
     *
     * @ref RN-S1.1.3: clarificationEndDate e impeachmentEndDate sao calculados
     * como `disputeStartDate - 3 dias uteis` no fluxo de cadastro de licitacao.
     * Espelho do BE — manter logica identica.
     */
    static subBrazilianBusinessDays(startDate: Date, days: number): Date {
        if (days < 0) {
            throw new Error(`subBrazilianBusinessDays requer days >= 0 (recebido: ${days})`);
        }
        const result = new Date(startDate);
        let subtracted = 0;
        while (subtracted < days) {
            result.setDate(result.getDate() - 1);
            if (this.isBrazilianBusinessDay(result)) {
                subtracted++;
            }
        }
        return result;
    }

    static isValidYear(year: number): boolean { return year > 0; }
    static isLeapYear(year: number): boolean { return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0); }
    static isValidMonth(month: number): boolean { return month >= 1 && month <= 12; }
    static isValidDay(month: number): boolean { return month >= 1 && month <= 31; }
    static isValidDateParts(year: number, month: number, day: number): boolean {
        if (!this.isValidYear(year) || !this.isValidMonth(month)) return false;
        const daysInMonth = [31, this.isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        return day >= 1 && day <= daysInMonth[month - 1];
    }
    static isValidHour(hour: number): boolean { return hour >= 0 && hour <= 23; }
    static isValidMinute(minute: number): boolean { return minute >= 0 && minute <= 59; }
    static isValidSecond(second: number): boolean { return second >= 0 && second <= 59; }
    static isValidMillisecond(millisecond: number): boolean { return millisecond >= 0 && millisecond <= 999; }

    // Date-only helpers (ISO yyyy-MM-dd) brought from date.ts
    static toISODate(date?: string): string | undefined {
        return date ? `${date}T00:00:00.000Z` : undefined;
    }

    static fromISODate(iso?: string): string | undefined {
        return iso ? iso.slice(0, 10) : undefined;
    }

    /**
     * Converts a date value to a `<input type="date">` compatible string (yyyy-MM-dd).
     * Returns empty string when the value is missing or invalid.
     */
    static toDateInputValue(value?: string | Date | null): string {
        if (!value) return '';
        const date = typeof value === 'string' ? new Date(value) : value;
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
        return date.toISOString().slice(0, 10);
    }

    /**
     * Converts a date value to a `<input type="datetime-local">` compatible string (yyyy-MM-ddTHH:mm).
     * Adjusts for the local timezone offset so the displayed value matches local time.
     * Returns empty string when the value is missing or invalid.
     */
    static toDateTimeInputValue(value?: string | Date | null): string {
        if (!value) return '';
        const date = typeof value === 'string' ? new Date(value) : value;
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
        const tzOffset = date.getTimezoneOffset() * 60 * 1000;
        const local = new Date(date.getTime() - tzOffset);
        return local.toISOString().slice(0, 16);
    }

    static nowDate(): string {
        return new Date().toISOString().slice(0, 10);
    }

    // Adds days to a date-only string (yyyy-MM-dd) and returns a date-only string
    static addDaysToDateString(base: string, days: number): string {
        const d = new Date(`${base}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + days);
        return d.toISOString().slice(0, 10);
    }

    static normalizeDateInput(value?: string | null): string | undefined {
        if (typeof value !== 'string') {
            return undefined;
        }

        const trimmed = value.trim();
        if (!trimmed || trimmed.toLowerCase() === 'invalid date') {
            return undefined;
        }

        const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

        if (ISO_DATE_ONLY_REGEX.test(trimmed)) {
            return trimmed;
        }

        const parsed = new Date(trimmed);
        if (Number.isNaN(parsed.valueOf())) {
            return undefined;
        }

        const year = parsed.getUTCFullYear();
        const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
        const day = String(parsed.getUTCDate()).padStart(2, '0');
        const normalized = `${year}-${month}-${day}`;

        return ISO_DATE_ONLY_REGEX.test(normalized) ? normalized : undefined;
    }

    static isAdult(value?: string | Date | null, minimumAge = 18): boolean {
        if (!value) {
            return false;
        }

        const date = value instanceof Date ? value : new Date(value);
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return false;
        }

        const today = new Date();
        let age = today.getUTCFullYear() - date.getUTCFullYear();
        const monthDiff = today.getUTCMonth() - date.getUTCMonth();
        const dayDiff = today.getUTCDate() - date.getUTCDate();

        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age -= 1;
        }

        return age >= minimumAge;
    }

    static toLocalizedDateTimeString(
        value?: string | Date | null,
        locale: string = 'pt-BR',
        options?: Intl.DateTimeFormatOptions,
    ): string {
        if (!value) {
            return '—';
        }

        const date = typeof value === 'string' ? new Date(value) : value;
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return '—';
        }

        try {
            return date.toLocaleString(locale, options ?? { dateStyle: 'short', timeStyle: 'short' });
        } catch (_error) {
            return date.toISOString();
        }
    }

    /**
     * TODO(bug-40-2026-04-22): validar se deve ser REMOVIDA ou tem caso de uso legítimo.
     *
     * Foi adicionada em commit `2b96f10e` (21/04 manhã) com a intenção de "preservar
     * wall-clock local" — mas quebrou a exibição de datas em AuctioneerView/SupplierView
     * `tab-general-info.tsx`. Ela IGNORA o sufixo Z de ISO UTC e usa as partes da string
     * como componentes de Date LOCAL (`new Date(year, month, day, hour, ...)`), efetivamente
     * tratando UTC como horário local.
     *
     * Quando o BD grava `20:05Z` UTC e o usuário está em Brasília (UTC-3), esta função
     * devolve `20:05 BRT` em vez do correto `17:05 BRT`. Sintoma: "+3h na exibição".
     *
     * Callers que usavam esta função foram movidos pra `new Date(value) + toLocaleString`
     * com `timeZone: 'America/Sao_Paulo'` em 2026-04-22. Grep antes de deletar:
     *   - `src/domains/pages/4.1-publictender-module-request/PublicTenders/Auctions/**`
     *     (já migrado)
     *   - verificar bulk em `src/**` por eventuais callers externos antes de remover
     *
     * Se houver cenário legítimo onde o backend grava datas SEM TZ suffix significando
     * "literal local" (e.g., backfill/importação legacy), este util resolve esse caso.
     * Documentar onde, ou renomear pra `parseNaiveDateTimeAsLocal` pra deixar óbvio.
     */
    static parseDateTimePreservingLocalTime(value?: string | Date | null): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }

        const brMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ T]+(\d{2}):(\d{2})(?::(\d{2}))?)?$/);
        if (brMatch) {
            const [, dayText, monthText, yearText, hourText = '0', minuteText = '0', secondText = '0'] = brMatch;
            const parsed = new Date(
                Number(yearText),
                Number(monthText) - 1,
                Number(dayText),
                Number(hourText),
                Number(minuteText),
                Number(secondText),
                0,
            );
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        const isoMatch = trimmed.match(
            /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i,
        );
        if (isoMatch) {
            const [, yearText, monthText, dayText, hourText = '0', minuteText = '0', secondText = '0', millisecondText = '0'] = isoMatch;
            const parsed = new Date(
                Number(yearText),
                Number(monthText) - 1,
                Number(dayText),
                Number(hourText),
                Number(minuteText),
                Number(secondText),
                Number(millisecondText.padEnd(3, '0')),
            );
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        const parsed = new Date(trimmed);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    static getHourInTimeZone(
        value: Date = new Date(),
        timeZone = 'America/Sao_Paulo',
        locale = 'pt-BR',
    ): number | null {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
            return null;
        }

        try {
            const hourText = new Intl.DateTimeFormat(locale, {
                hour: 'numeric',
                hour12: false,
                timeZone,
            }).format(value);
            const hour = Number(hourText);
            if (!Number.isFinite(hour) || hour < 0 || hour > 23) {
                return null;
            }
            return hour;
        } catch (_error) {
            return null;
        }
    }

    // Date-only friendly formatter
    static toLocalizedDateString(value?: string | Date | null, locale: string = 'pt-BR', options?: Intl.DateTimeFormatOptions): string {
        if (!value) {
            return '—';
        }

        const date = typeof value === 'string' ? new Date(value) : value;
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return '—';
        }

        try {
            return date.toLocaleDateString(locale, options ?? { dateStyle: 'short' });
        } catch (_error) {
            return date.toISOString().slice(0, 10);
        }
    }

    // Relative time helper (up to 30 days window)
    static timeAgo(value?: string | Date | null): string {
        if (!value) return '—';

        const date = typeof value === 'string'
            ? new Date(String(value).replace(/-/g, '/').replace(/[TZ]/g, ' '))
            : value;

        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';

        const diff = (Date.now() - date.getTime()) / 1000;
        const dayDiff = Math.floor(diff / 86400);

        if (Number.isNaN(dayDiff) || dayDiff < 0 || dayDiff >= 31) {
            return this.toLocalizedDateString(date, 'en-US', { month: 'long', day: '2-digit', year: 'numeric' });
        }

        if (dayDiff === 0) {
            if (diff < 60) return 'just now';
            if (diff < 120) return '1 minute ago';
            if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
            if (diff < 7200) return '1 hour ago';
            if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
        }

        if (dayDiff === 1) return 'Yesterday';
        if (dayDiff < 7) return `${dayDiff} days ago`;
        return `${Math.ceil(dayDiff / 7)} weeks ago`;
    }

    // Difference between a target date-time and now
    static toTimeDeltaFromNow(value?: string | Date | null): { days: string; hours: string; minutes: string; seconds: string } {
        const target = typeof value === 'string' ? new Date(value) : value;
        if (!(target instanceof Date) || Number.isNaN(target.getTime())) {
            return { days: '00', hours: '00', minutes: '00', seconds: '00' };
        }

        const milliseconds = Math.floor(target.getTime() - Date.now());
        const days = Math.round(milliseconds / 86400000);
        const hours = Math.round((milliseconds % 86400000) / 3600000);
        let minutes = Math.round(((milliseconds % 86400000) % 3600000) / 60000);
        const seconds = Math.round((((milliseconds % 86400000) % 3600000) % 60000) / 1000);

        if (seconds < 30 && seconds >= 0) {
            minutes += 1;
        }

        const formatPart = (num: number) => {
            const str = num.toString();
            return str.length < 2 ? `0${str}` : str;
        };

        return {
            days: formatPart(days),
            hours: formatPart(hours),
            minutes: formatPart(minutes),
            seconds: formatPart(seconds),
        };
    }
}
